import { invoicesApi } from 'api/invoice/invoice';
import { errorMessageFactory } from 'factories/api';
import { invoiceDraftFactory, invoiceDraftToRequestFactory } from 'factories/invoices';
import { fetchFullProjectsIfNeeded } from 'redux/modules/projects.actions';
import { getMaybeHasMoreStatus } from 'redux/reusable/pagination';
import { xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import { getIsUserInExperimentalFeatureGroup } from 'redux/selectors/user';
import type * as types from 'types/invoice';
import { InvoiceStatus } from 'types/invoice';
import type { Thunk } from 'types/redux';
import {
  getInvoiceDraft,
  getInvoicePagination,
  getInvoiceQuery,
  getInvoiceRules,
  getInvoices,
  getSortBy,
} from './invoice.selectors';
import {
  clearInvoiceDraft,
  clearPagination,
  clearQuery,
  createInvoiceRulesDraft,
  deletedInvoice,
  fetchedInvoice,
  fetchedInvoiceRules,
  fetchedInvoices,
  INVOICE_PAGE_LIMIT,
  invoiceXhrModule,
  paginationActions,
  SERVER_SIDE_FIELDS,
  setInvoiceDraft,
  setInvoicePreview,
} from './invoice.types';
import { showToast } from './toasts.actions';

const { wrapFetching, wrapSaving } = invoiceXhrModule;

interface FetchInvoicesParams {
  fetchKey?: string;
  force?: boolean;
}

export function fetchInvoicesSmartly(options: FetchInvoicesParams): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const { fetchKey, force } = options;
    const state = getState();
    const key = `invoice-${fetchKey}`;
    const xhrStatus = xhrStatusQueryInfo(state.invoice.xhrStatus, { key });

    if (!xhrStatus.shouldFetch && !force) {
      return Promise.resolve();
    }
    const paginationState = getInvoicePagination(state);
    const query = getInvoiceQuery(state);
    const sortBy = getSortBy(state);

    const params = {
      order: paginationState.order,
      offset: paginationState.offset,
      limit: INVOICE_PAGE_LIMIT,
      project_ids: query.projectIds,
      sort: SERVER_SIDE_FIELDS.includes(sortBy.field) ? sortBy.field : undefined,
      user_ids: query.userIds,
      status: query.status !== 'all' ? query.status : null,
    };

    try {
      const invoices = await dispatch(wrapFetching(invoicesApi.getInvoices(params), key));
      const maybeHasMore = getMaybeHasMoreStatus(paginationState, invoices.length);
      dispatch(fetchedInvoices({ invoices, freshState: force }));
      dispatch(
        paginationActions.setParams({
          offset: force ? invoices.length : invoices.length + paginationState.offset,
        })
      );
      dispatch(paginationActions.setMaybeHasMore({ maybeHasMore }));
    } catch (error) {
      dispatch(showToast({ type: 'warning', message: errorMessageFactory(error) }));
    }
  };
}

export function resetInvoicesState(): Thunk<void> {
  return dispatch => {
    dispatch(clearQuery());
    dispatch(clearPagination());
  };
}

export function fetchInvoiceById(id: number): Thunk<Promise<types.RawInvoice>> {
  return async dispatch => {
    const invoice = await dispatch(wrapFetching(invoicesApi.getInvoiceById(id)));
    dispatch(fetchedInvoice(invoice));
    return invoice;
  };
}

export function deleteInvoiceById(id: number): Thunk<Promise<void>> {
  return async dispatch => {
    await dispatch(wrapFetching(invoicesApi.deleteInvoiceById(id)));
    try {
      dispatch(deletedInvoice({ id }));
    } catch (error) {
      dispatch(showToast({ type: 'warning', message: errorMessageFactory(error) }));
    }
  };
}

export function createInvoicePreview(
  invoiceRequest: types.InvoiceRequestBody
): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const preview = await dispatch(wrapSaving(invoicesApi.createInvoicePreview(invoiceRequest)));
      dispatch(setInvoicePreview(preview));

      const projectIds = preview.project_ids.map(details => details.project_id);
      const skippedProjectIds = preview.skipped_project_ids.map(details => details.id);

      dispatch(fetchFullProjectsIfNeeded([...projectIds, ...skippedProjectIds]));
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function createInvoicePreviewFromDraft(): Thunk {
  return (dispatch, getState) => {
    const draft = getInvoiceDraft(getState());
    if (draft) {
      const invoiceRequest = invoiceDraftToRequestFactory(draft);
      dispatch(createInvoicePreview(invoiceRequest));
    }
  };
}

export function createInvoice(invoiceRequest: types.InvoiceRequestBody): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const { skipped_project_ids, skipped_client_ids, creating_invoices, invoice_ids, error } =
      await dispatch(wrapSaving(invoicesApi.createInvoice(invoiceRequest)));

    try {
      // todo, these responses aren't finalised yet, either way need to update copy
      const skippedSomeInvoices = skipped_project_ids?.length > 0 || skipped_client_ids?.length > 0;

      if (skippedSomeInvoices && creating_invoices > 0) {
        dispatch(
          showToast({
            type: 'warning',
            message: error,
          })
        );
      } else if ((skippedSomeInvoices && creating_invoices === 0) || error) {
        dispatch(
          showToast({
            type: 'danger',
            message: error,
          })
        );
      } else {
        dispatch(
          showToast({
            type: 'success',
            message: creating_invoices > 1 ? 'Creating invoices' : 'Creating invoice',
          })
        );

        // Generate drafts of our new invoices while we await signal from the backend that they're ready to fully load
        const newInvoices: Array<types.RawInvoice> = invoice_ids.map(id =>
          invoiceDraftFactory({ id, status: InvoiceStatus.Incomplete })
        );

        dispatch(fetchedInvoices({ invoices: newInvoices }));
      }

      if (getIsUserInExperimentalFeatureGroup(getState()) && !!creating_invoices) {
        dispatch(
          showToast({
            type: 'warning',
            message:
              'Invoices are being created in the background, if you are seeing this message in a non-production environment, refresh the page to see them',
          })
        );
      }

      dispatch(clearInvoiceDraft());
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function createInvoiceFromDraft(): Thunk {
  return (dispatch, getState) => {
    const draft = getInvoiceDraft(getState());
    if (draft) {
      const invoiceRequest = invoiceDraftToRequestFactory(draft);
      dispatch(createInvoice(invoiceRequest));
    }
  };
}

function setInvoicesAsSyncing(invoices: Array<types.RawInvoice>): Thunk {
  return dispatch => {
    const awaitingSync = invoices.map(invoice => ({ ...invoice, isAwaitingSync: true }));
    dispatch(fetchedInvoices({ invoices: awaitingSync }));
  };
}

export function syncInvoice(invoice: types.RawInvoice): Thunk<Promise<types.RawInvoice | null>> {
  return async (dispatch, getState) => {
    const client = invoice.project?.client;
    const state = getState();

    try {
      const invoiceResponse = await dispatch(
        wrapFetching(invoicesApi.syncInvoiceToDefaultApp(invoice.id))
      );
      dispatch(fetchedInvoice(invoiceResponse));
      const invoicesForClient = getInvoices(state).filter(
        inv => inv?.project?.client?.id === client?.id
      );
      dispatch(setInvoicesAsSyncing(invoicesForClient));

      return invoiceResponse;
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function fetchInvoiceRules(): Thunk<Promise<types.RawInvoiceRules>> {
  return async dispatch => {
    const rules = await dispatch(wrapFetching(invoicesApi.getInvoiceRules()));
    dispatch(fetchedInvoiceRules(rules));
    return rules;
  };
}

export function updateInvoiceRules(
  rules: types.UpdateInvoiceRulesRequest
): Thunk<Promise<types.RawInvoiceRules>> {
  return async dispatch => {
    const updatedRules = await dispatch(wrapSaving(invoicesApi.updateInvoiceRules(rules)));
    dispatch(fetchedInvoiceRules(updatedRules));
    return updatedRules;
  };
}

export function initializeInvoiceRulesDraft(draft: types.RawInvoiceRules) {
  return createInvoiceRulesDraft(draft);
}

export function createInvoiceDraft(seed: Partial<types.RawInvoice>): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const rules = getInvoiceRules(state);
    const draft = getInvoiceDraft(state);
    const draftWithRules = invoiceDraftFactory({ ...draft, ...seed }, rules);

    dispatch(setInvoiceDraft(draftWithRules));
  };
}
